import { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import api from "../../../utils/api";

function emptyLine(sortOrder) {
    return {
        direction: "expense",
        expense_type_id: null,
        description: "",
        default_amount: "",
        sort_order: sortOrder,
    };
}

export default function ExpenseTemplatesManagerDialog({ open, onClose }) {
    const [view, setView] = useState("list");
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        name: "",
        notes: "",
        is_active: true,
        lines: [emptyLine(0)],
    });
    const [lineErrors, setLineErrors] = useState({});
    const [nameError, setNameError] = useState("");

    const [expenseTypes, setExpenseTypes] = useState([]);

    const loadList = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await api.get("admin/finance-expense-templates");
            setList(res.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load templates.");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadExpenseTypes = useCallback(async () => {
        try {
            const res = await api.get("admin/expense-types/list");
            setExpenseTypes(res.data?.data || []);
        } catch (err) {
            console.error("Failed to load expense types", err);
        }
    }, []);

    useEffect(() => {
        if (!open) {
            setView("list");
            setEditingId(null);
            setError("");
            return;
        }
        loadList();
        loadExpenseTypes();
    }, [open, loadList, loadExpenseTypes]);

    const openNew = () => {
        setEditingId(null);
        setForm({
            name: "",
            notes: "",
            is_active: true,
            lines: [emptyLine(0)],
        });
        setView("edit");
        setError("");
        setLineErrors({});
        setNameError("");
    };

    const openEdit = async (id) => {
        setLoading(true);
        setError("");
        try {
            const res = await api.get(`admin/finance-expense-templates/${id}`);
            const d = res.data?.data || {};
            setEditingId(id);
            setLineErrors({});
            setNameError("");
            setForm({
                name: d.name || "",
                notes: d.notes || "",
                is_active: d.is_active !== false,
                lines:
                    Array.isArray(d.lines) && d.lines.length
                        ? d.lines.map((ln, i) => ({
                              direction: ln.direction || "expense",
                              expense_type_id: ln.expense_type_id || ln.expense_type?.id || null,
                              description: ln.description || "",
                              default_amount: ln.default_amount != null ? String(ln.default_amount) : "",
                              sort_order: ln.sort_order ?? i,
                          }))
                        : [emptyLine(0)],
            });
            setView("edit");
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load template.");
        } finally {
            setLoading(false);
        }
    };

    const removeTemplate = async (id) => {
        if (!window.confirm("Delete this template?")) return;
        setError("");
        try {
            await api.delete(`admin/finance-expense-templates/${id}`);
            await loadList();
        } catch (err) {
            setError(err.response?.data?.message || "Could not delete.");
        }
    };

    const addLine = () => {
        setForm((prev) => ({
            ...prev,
            lines: [...prev.lines, emptyLine(prev.lines.length)],
        }));
    };

    const updateLine = (index, patch) => {
        setForm((prev) => ({
            ...prev,
            lines: prev.lines.map((ln, i) => (i === index ? { ...ln, ...patch } : ln)),
        }));
        // Clear the inline error for the touched field as the user types.
        if (patch && Object.keys(patch).some((key) => key === "default_amount" || key === "description")) {
            setLineErrors((prev) => {
                const next = { ...prev };
                const fields = next[index] ? { ...next[index] } : {};
                if (Object.prototype.hasOwnProperty.call(patch, "default_amount")) {
                    delete fields.default_amount;
                }
                if (Object.prototype.hasOwnProperty.call(patch, "description")) {
                    delete fields.description;
                }
                if (Object.keys(fields).length === 0) {
                    delete next[index];
                } else {
                    next[index] = fields;
                }
                return next;
            });
        }
    };

    const removeLine = (index) => {
        setForm((prev) => ({
            ...prev,
            lines: prev.lines.filter((_, i) => i !== index),
        }));
        setLineErrors((prev) => {
            const next = {};
            Object.entries(prev).forEach(([k, v]) => {
                const idx = Number(k);
                if (idx === index) return;
                next[idx > index ? idx - 1 : idx] = v;
            });
            return next;
        });
    };

    const save = async () => {
        const nextLineErrors = {};
        let hasError = false;

        const trimmedName = form.name.trim();
        if (!trimmedName) {
            setNameError("Name is required.");
            hasError = true;
        } else {
            setNameError("");
        }

        form.lines.forEach((ln, i) => {
            const lineErr = {};
            const description = String(ln.description || "").trim();
            const amountNum = Number(ln.default_amount);

            if (!description) {
                lineErr.description = "Description is required.";
            }
            if (ln.default_amount === "" || ln.default_amount === null || ln.default_amount === undefined) {
                lineErr.default_amount = "Amount is required.";
            } else if (!Number.isFinite(amountNum)) {
                lineErr.default_amount = "Amount must be a number.";
            } else if (amountNum <= 0) {
                lineErr.default_amount = "Amount must be greater than 0.";
            }

            if (Object.keys(lineErr).length) {
                nextLineErrors[i] = lineErr;
                hasError = true;
            }
        });

        setLineErrors(nextLineErrors);

        if (hasError) {
            setError("Please fix the highlighted fields.");
            return;
        }

        const linesPayload = form.lines.map((ln, i) => ({
            direction: ln.direction,
            expense_type_id: ln.direction === "expense" ? (ln.expense_type_id || null) : null,
            description: String(ln.description || "").trim(),
            default_amount: Number(ln.default_amount),
            sort_order: ln.sort_order ?? i,
        }));

        setSaving(true);
        setError("");
        try {
            const body = {
                name: trimmedName,
                notes: form.notes?.trim() || null,
                is_active: form.is_active,
                lines: linesPayload,
            };
            if (editingId) {
                await api.put(`admin/finance-expense-templates/${editingId}`, body);
            } else {
                await api.post("admin/finance-expense-templates", body);
            }
            setView("list");
            setEditingId(null);
            setLineErrors({});
            setNameError("");
            await loadList();
        } catch (err) {
            // Surface server-side validation errors per line if returned.
            const serverErrors = err.response?.data?.errors;
            if (serverErrors && typeof serverErrors === "object") {
                const fieldLineErrors = {};
                Object.entries(serverErrors).forEach(([key, msgs]) => {
                    const m = key.match(/^lines\.(\d+)\.(\w+)$/);
                    if (!m) return;
                    const idx = Number(m[1]);
                    const field = m[2] === "default_amount" ? "default_amount" : m[2] === "description" ? "description" : null;
                    if (!field) return;
                    fieldLineErrors[idx] = fieldLineErrors[idx] || {};
                    fieldLineErrors[idx][field] = Array.isArray(msgs) ? msgs[0] : String(msgs);
                });
                if (Object.keys(fieldLineErrors).length) {
                    setLineErrors(fieldLineErrors);
                }
            }
            setError(
                err.response?.data?.message ||
                    (typeof err.response?.data === "string" ? err.response.data : null) ||
                    "Could not save template."
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
            <DialogTitle>
                {view === "list" ? "Expense templates" : editingId ? "Edit template" : "New template"}
            </DialogTitle>
            <DialogContent>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

                {view === "list" ? (
                    <Box sx={{ mt: 1 }}>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={{ mb: 2 }}>
                            New template
                        </Button>
                        {loading ? <Typography color="text.secondary">Loading…</Typography> : null}
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell align="center">Lines</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {list.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell align="center">{row.lines_count ?? 0}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" aria-label="Edit" onClick={() => openEdit(row.id)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" aria-label="Delete" onClick={() => removeTemplate(row.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!list.length && !loading ? (
                                    <TableRow>
                                        <TableCell colSpan={3}>
                                            <Typography variant="body2" color="text.secondary">
                                                No templates yet. Create one for recurring monthly expenses.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </TableBody>
                        </Table>
                    </Box>
                ) : (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => !saving && setView("list")}>
                            Back to list
                        </Button>
                        <FormControl error={!!nameError} fullWidth>
                            <OutlinedInput
                                size="small"
                                placeholder="Template name"
                                value={form.name}
                                onChange={(e) => {
                                    setForm((p) => ({ ...p, name: e.target.value }));
                                    if (nameError) setNameError("");
                                }}
                                fullWidth
                            />
                            {nameError ? <FormHelperText>{nameError}</FormHelperText> : null}
                        </FormControl>
                        <OutlinedInput
                            size="small"
                            placeholder="Notes (optional)"
                            multiline
                            minRows={2}
                            value={form.notes}
                            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.is_active}
                                    onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                                />
                            }
                            label="Active"
                        />
                        <Typography variant="subtitle2">Lines</Typography>
                        {form.lines.map((ln, index) => {
                            const selectedType = expenseTypes.find((t) => Number(t.id) === Number(ln.expense_type_id)) || null;
                            const lineErr = lineErrors[index] || {};
                            return (
                                <Stack
                                    key={index}
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    alignItems={{ sm: "flex-start" }}
                                >
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Direction</InputLabel>
                                        <Select
                                            label="Direction"
                                            value={ln.direction}
                                            onChange={(e) => updateLine(index, { direction: e.target.value, expense_type_id: e.target.value === "income" ? null : ln.expense_type_id })}
                                        >
                                            <MenuItem value="expense">Expense</MenuItem>
                                            <MenuItem value="income">Income</MenuItem>
                                        </Select>
                                    </FormControl>
                                    {ln.direction === "expense" ? (
                                        <Autocomplete
                                            size="small"
                                            options={expenseTypes}
                                            value={selectedType}
                                            getOptionLabel={(option) => option?.name || ""}
                                            isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                                            onChange={(_, val) => updateLine(index, { expense_type_id: val?.id || null })}
                                            sx={{ minWidth: 200 }}
                                            renderInput={(params) => <TextField {...params} placeholder="Expense type" />}
                                        />
                                    ) : null}
                                    <FormControl error={!!lineErr.description} sx={{ flex: 1 }}>
                                        <OutlinedInput
                                            size="small"
                                            placeholder="Description"
                                            value={ln.description}
                                            onChange={(e) => updateLine(index, { description: e.target.value })}
                                            fullWidth
                                        />
                                        {lineErr.description ? (
                                            <FormHelperText>{lineErr.description}</FormHelperText>
                                        ) : null}
                                    </FormControl>
                                    <FormControl error={!!lineErr.default_amount} sx={{ width: 160 }}>
                                        <OutlinedInput
                                            size="small"
                                            type="number"
                                            placeholder="Amount"
                                            inputProps={{ min: 0.01, step: 0.01 }}
                                            value={ln.default_amount}
                                            onChange={(e) => updateLine(index, { default_amount: e.target.value })}
                                            startAdornment={<InputAdornment position="start">֏</InputAdornment>}
                                        />
                                        {lineErr.default_amount ? (
                                            <FormHelperText>{lineErr.default_amount}</FormHelperText>
                                        ) : null}
                                    </FormControl>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => removeLine(index)}
                                        disabled={form.lines.length <= 1}
                                        sx={{ mt: { sm: 0.5 } }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            );
                        })}
                        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addLine}>
                            Add line
                        </Button>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {view === "edit" ? (
                    <>
                        <Button onClick={() => !saving && setView("list")} disabled={saving}>Cancel</Button>
                        <Button variant="contained" onClick={save} disabled={saving}>
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </>
                ) : (
                    <Button onClick={onClose}>Close</Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
